import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useT } from "@/context/LanguageContext";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

export default function Services({ services = [] }) {
  const { t, ui } = useT();
  return (
    <section id="servicios" className="relative py-24 md:py-40 bg-[color:var(--gcs-ivory)]" data-testid="services-section">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-14 mb-16 md:mb-24">
          <div className="md:col-span-4">
            <p className="overline text-[color:var(--gcs-gold)] mb-4">{ui("services.chapter")}</p>
            <div className="flex items-baseline gap-4">
              <span className="font-serif italic text-6xl md:text-7xl text-[color:var(--gcs-gold)]/40">/06</span>
            </div>
          </div>
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
            className="md:col-span-8"
          >
            <h2 className="font-serif text-4xl md:text-6xl leading-[1.02] tracking-tight text-[color:var(--gcs-emerald)]">
              {ui("services.h1_a")}<em className="text-[color:var(--gcs-gold)]">{ui("services.h1_em")}</em>{ui("services.h1_b")}
            </h2>
            <p className="mt-6 text-base md:text-lg text-[color:var(--gcs-emerald)]/75 max-w-2xl leading-relaxed">
              {ui("services.lead")}
            </p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {services.map((s, i) => (
            <motion.a
              key={s.id || i}
              href="#contacto"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className="group card-lift block bg-[color:var(--gcs-ivory-2)] border border-[color:var(--gcs-line)] rounded-sm overflow-hidden"
              data-testid={`service-card-${i}`}
            >
              <div className="zoom-frame aspect-[4/3] bg-[color:var(--gcs-emerald)]">
                <img src={s.image_url} alt={t(s.name)} className="w-full h-full object-cover opacity-90 group-hover:opacity-100" />
              </div>
              <div className="p-6 md:p-8">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <h3 className="font-serif text-2xl md:text-3xl text-[color:var(--gcs-emerald)] leading-tight">
                    {t(s.name)}
                  </h3>
                  <ArrowUpRight className="w-5 h-5 text-[color:var(--gcs-gold)] shrink-0 mt-1 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </div>
                <p className="text-sm text-[color:var(--gcs-emerald)]/75 leading-relaxed mb-5">{t(s.description)}</p>
                <ul className="space-y-1.5 mb-5">
                  {(s.features || []).map((f, k) => (
                    <li key={k} className="text-xs text-[color:var(--gcs-emerald)]/70 flex items-center gap-2">
                      <span className="h-px w-4 bg-[color:var(--gcs-gold)]" />
                      {t(f)}
                    </li>
                  ))}
                </ul>
                <div className="flex items-center justify-between pt-4 border-t border-[color:var(--gcs-line)]">
                  <span className="font-serif italic text-lg text-[color:var(--gcs-emerald)]">{t(s.price_from)}</span>
                  <span className="overline text-[color:var(--gcs-gold)]">{ui("cta.quote")} →</span>
                </div>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}
